// React imports
import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import productService from "services/product-service";
import {AuthContext} from "context"; 

// E4A Components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";

// Dashboard & Material UI components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Custom Components & functions
import PartCodeDetails from "../../Inventory-management/components/partcode-details";
import FileUploads from "../components/file-uploads";
import ProductDetailsFields from "../components/input-product-details";
import LocationFields from "../components/location-fields";
import SelectOrder from "../components/select-order";
import {
    validateFilesData,
    validateProductData,
} from "../components/validators.js";

export default function EditProduct({ }) {
    const { id } = useParams();
    const [filesData, setFilesData] = useState([]);
    const [product, setProduct] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [productErrors, setProductErrors] = useState({});
    const [initialFiles, setInitialFiles] = useState([]);
    const [displayFilesSection, setDisplayFilesSection] = useState(false);

    const authContext = useContext(AuthContext);
    // Fetch product data to edit
    useEffect(() => {
        (async () => {
            try {
                const productResponse = await productService.getProductById(id);
                const productData = productResponse.data.attributes;

                // Adjust fetched data to match frontend expectations
                productData.shelfLifeUnit = productData.shelfLife[1];
                productData.shelfLife = productData.shelfLife[0];
                productData.orderNum = productData.order?.order_num || "";
                
                setProduct(productData);

                if (productData.certifications && productData.certifications.length > 0) {
                    setInitialFiles(productData.certifications || []);
                    setFilesData(formatFilesData(productData.certifications));
                }

                setDisplayFilesSection(true);

            } catch (error) {
                console.log("Error fetching product data: ", error);
            }
        })();
    }, []);

    const navigate = useNavigate();

    // Handles changes in the product state
    const changeHandler = (name, value) => {
        setProduct({
            ...product,
            [name]: value,
        })
    }

    // Allows child components to update multiple fields in the product state at once
    const batchChangeHandler = (updates) => {
        setProduct({
            ...product,
            ...updates,
        })
    }

    // Fields tied to the files to upload.
    const fields = [
        { label: 'File Name', name: 'fileName' },
        { label: 'Issue Date', name: 'issueDate', type: 'date' },
        { label: 'Expiry Date', name: 'expiryDate', type: 'date' },
    ]

    const errorProps = {
        'border': '1px solid red',
        'borderRadius': '4px',
        'padding': '8px',
    }
    // Handles the upload of the files upon submission
    const uploadFiles = async () => {
        let response = [];
        for (const fileData of filesData) {
            if (fileData.fileAdded) {
                // File was already uploaded, skip re-upload
                response.push({
                    issueDate: fileData.issueDate,
                    expiryDate: fileData.expiryDate,
                    key: fileData.key,
                });
            } else {
                const payload = {
                    file: fileData.file,
                    data: {
                        attributes: {
                            fileName: fileData.fileName,
                            organization: product.organizationName,
                            folderPath: product.serialNumber,
                        }
                    }
                }
                const uploadResp = await productService.uploadProductFile(payload);

                response.push({
                    issueDate: fileData.issueDate,
                    expiryDate: fileData.expiryDate,
                    key: uploadResp.s3Key
                    })
            }
        }
        return response;
    }

    // returns files that were removed by the user during editing or explicitly marked to delete.
    const findFilesToDelete = () => {
        // Helper to derive a comparable name from any file object
        const getName = (f) => {
            const name = f?.fileName ?? (typeof f?.key === "string" ? f.key.split("/").pop() : "");
            return (name || "").trim();
        };

        // Build a set of current file names from filesData (normalize by derived name)
        const currentNames = new Set((filesData || []).map(getName));

        // Names explicitly flagged for deletion (must exist in filesData)
        const flaggedNames = new Set(
            (filesData || [])
                .filter((f) => f?.toDelete)
                .map(getName)
        );

        // Return initial files that were removed, or are present but flagged for deletion
        return (initialFiles || []).filter((f) => {
            const name = getName(f);
            const removed = !currentNames.has(name);
            const flagged = flaggedNames.has(name);
            return removed || flagged;
        });
    }

    // Removes from the S3 the files that were flagged for deletion
    const deleteFiles = async (filesToDelete) => {
        try {
            const fileKeys = filesToDelete.map((f) => f.key);
            const payload = {
                data : {
                    attributes: {
                        fileKeys: fileKeys
                    }
                }
            }
            await productService.deleteFilesFromS3(payload);
        } catch (error) {
            console.error("Error deleting files: ", error);
        }
    }

    const formatFilesData = (files) => {
        let formatted = [];
        // Convert backend file payloads to objects with Dayjs dates and a derived fileName.
        const adapter = new AdapterDayjs();
        if (!Array.isArray(files)) return formatted;


        for (const f of files) {
            const issue = f?.issueDate ? adapter.date(f.issueDate) : null;
            const expiry = f?.expiryDate ? adapter.date(f.expiryDate) : null;
            const name =
                f?.fileName ??
                (typeof f?.key === "string" ? f.key.split("/").pop() : "") ?? "";

            formatted.push({
                ...f,
                fileName: name,
                issueDate: issue,
                expiryDate: expiry,
                fileAdded: true, // Mark as already uploaded
            });
        }

        return formatted;
    }

    // Form submission handler
    const onSubmit = async (e) => {
        e.preventDefault();
        // Clear previous errors
        setProductErrors({});
        // Validate inputs
        const productValid = validateProductData(product);
        const filesValid = validateFilesData(filesData);

        console.log("Validation Results:", { productValid, filesValid });

        const errors = {
            ...(productValid || {}),
            ...(filesValid || {})
        };

        if (Object.keys(errors).length > 0) {
            setProductErrors(errors);
            return;
        }

        // If all validations pass, proceed with form submission
        try {
            setIsLoading(true);
            let uploadedFiles = [];

            const filesToDelete = findFilesToDelete();
            await deleteFiles(filesToDelete);

            if (Object.keys(filesData).length > 0) {
                uploadedFiles = await uploadFiles();
            }
            const payload = {
                data: {
                    attributes: {
                        ...product,
                        certifications: uploadedFiles || [],
                    },
                    logs: {
                        user: await authContext.getCurrentUser(),
                        comment: "" // You can add a comment here if needed
                    }
                }
            }

            await productService.updateProduct(id, payload);

            setIsLoading(false);
            navigate("/fulfillment-center");
        } catch (error) {
            console.error("Error adding product:", error);
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar breadcrumbTitle={product?.serialNumber && product.serialNumber.replace(/-/g, '–')} />

            <MDBox mt={5} mb={9}>
                <Grid container justifyContent="center" spacing={3}>

                    <Grid item xs={12}>

                        <MDBox mt={6} mb={6} textAlign="center">
                            <MDBox mb={1}>
                                {/* Title */}
                                <MDTypography variant="h3" fontWeight="bold">
                                    Edit Product
                                </MDTypography>
                            </MDBox>
                            {/* Subtitle */}
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                    These informations will be used to describe the product.
                                </MDTypography>
                            </MDBox>

                        </MDBox>
                    </Grid>

                    {isLoading ? (
                        <Grid item xs={12}>
                            <MDBox display="flex" justifyContent="center" alignItems="center" height="40vh">
                                <CircularProgress />
                            </MDBox>
                        </Grid>
                    ) : (<>
                        <Grid item xs={12} lg={9} justifyContent="space-around">
                            <MDBox>
                                {/* Part Code Details Component */}
                                {product?.partCode && (
                                    <PartCodeDetails id={product.partCode} />
                                )}
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} lg={9}>
                            {/* Card */}
                            <Card >
                                <MDBox
                                    component="form"
                                    onSubmit={onSubmit}
                                    p={3}
                                >

                                    {/* Product Details */}
                                    {Object.keys(product || {}).length > 0 && (
                                        <MDBox m={2} mb={3}>
                                            <MDTypography variant="h4" fontWeight="bold">
                                                Product Details:
                                            </MDTypography>
                                            <ProductDetailsFields
                                                changeHandler={changeHandler}
                                                product={product}
                                                errors={productErrors}
                                            />
                                        </MDBox>
                                    )}

                                    {/* Location Assignment */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                            Assign Location :
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            Clients cannot view location details.
                                        </MDTypography>

                                        {Object.keys(product || {}).length > 0 && (
                                            <MDBox mt={2} sx={productErrors.locationId ? errorProps : {}}>
                                                <LocationFields
                                                    product={product}
                                                    changeHandler={changeHandler}
                                                    batchChangeHandler={batchChangeHandler}
                                                    editMode={true}
                                                    commonProps={{ // Props common for the input fields
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        fullWidth: true,
                                                        marginBottom: "0.5rem",
                                                        marginTop: "0.5rem",
                                                    }}
                                                />
                                            </MDBox>
                                        )}

                                        {productErrors.locationId && (
                                            <MDTypography variant="caption" color="error" ml={0.5}>
                                                {productErrors.locationId}
                                            </MDTypography>
                                        )}

                                    </MDBox>

                                    {/* Organization Assignment */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                            Assign To Order :
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            All users within the selected order's organization will be able to see this product.
                                        </MDTypography>

                                        {Object.keys(product || {}).length > 0 && (
                                            <MDBox sx={productErrors.order ? errorProps : {}} mt={2}>
                                                <SelectOrder
                                                    changeHandler={batchChangeHandler}
                                                    selectedOrder={product.order}
                                                />
                                            </MDBox>
                                        )}

                                        {productErrors.order && (
                                            <MDTypography variant="caption" color="error" ml={0.5}>
                                                {productErrors.order}
                                            </MDTypography>
                                        )}

                                    </MDBox>

                                    {displayFilesSection && (
                                        // File Uploads Section 
                                        <MDBox m={2} mb={3}>
                                            <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                                Certifications Upload:
                                            </MDTypography>
                                            <MDBox mt={2}>
                                                <FileUploads
                                                    fields={fields}
                                                    errors={productErrors}
                                                    filesData={filesData}
                                                    setFilesData={setFilesData}
                                                />
                                            </MDBox>
                                        </MDBox>
                                    )}

                                    {/* Submit & Cancel buttons */}
                                    <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">

                                        {/* Cancel Button */}
                                        <MDBox mx={1}>
                                            <MDButton
                                                variant="gradient" color="dark" size="small" px={2} mx={2}
                                                onClick={() => navigate(-1)}
                                            >
                                                Cancel
                                            </MDButton>
                                        </MDBox>

                                        {/* Submit Button */}
                                        <MDBox mx={1} >
                                            <MDButton variant="gradient" color="dark" size="small" type="submit">
                                                Save
                                            </MDButton>
                                        </MDBox>
                                    </MDBox>

                                </MDBox>
                            </Card>

                        </Grid>
                    </>)}
                </Grid>
                <MDBox mt={5}>
                    <Footer />
                </MDBox>
            </MDBox>

        </DashboardLayout>
    )
}